import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, useWindowDimensions, Switch } from 'react-native';
import { FaCheck, FaHeart, FaComment, FaShare, FaEye, FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaFacebookF, FaExternalLinkAlt } from 'react-icons/fa';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';

let THEME = {
    primary: '#0a84ff',
    success: '#30d158',
    warning: '#ff9f0a',
    error: '#ff453a',
    purple: '#bf5af2',
    card: 'rgba(28, 28, 30, 0.7)',
    cardBorder: 'rgba(255, 255, 255, 0.08)',
    textMain: '#ffffff',
    textSecondary: 'rgba(235, 235, 245, 0.6)',
    textTertiary: 'rgba(235, 235, 245, 0.3)',
};

const buildPostPickerTheme = (theme) => ({
    primary: theme.accentBlue,
    success: theme.accentGreen,
    warning: theme.accentYellow,
    error: theme.accentRed,
    purple: theme.accentPurple,
    card: theme.bgCard,
    cardBorder: theme.border,
    textMain: theme.textPrimary,
    textSecondary: theme.textSecondary,
    textTertiary: theme.textMuted,
});

const PLATFORM_COLORS = {
    twitter: '#1da1f2',
    telegram: '#0088cc',
    tiktok: '#ff0050',
    instagram: '#e1306c',
    facebook_group: '#1877F2',
};

const PLATFORM_ICONS = {
    twitter: FaTwitter,
    telegram: FaTelegram,
    tiktok: FaTiktok,
    instagram: FaInstagram,
    facebook_group: FaFacebookF,
};

const formatNum = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
};

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}`;
};

const PostPickerGrid = ({
    posts,
    selectedIds,
    onToggle,
    onSelectAll,
    autoMonitor,
    onAutoMonitorChange,
    onConfirm,
    onClose,
    loading,
    platform,
    username,
    queueRemaining = 0,
}) => {
    const { theme: globalTheme } = useTheme();
    const { t } = useTranslation();
    Object.assign(THEME, buildPostPickerTheme(globalTheme));
    styles = getStyles();
    const { width } = useWindowDimensions();
    const columns = width >= 1024 ? 3 : 2;
    const PlatformIcon = PLATFORM_ICONS[platform] || FaInstagram;
    const selectedCount = selectedIds.size;
    const allSelected = posts.length > 0 && selectedCount === posts.length;
    const hasImages = platform === 'instagram' || platform === 'tiktok';

    if (loading) {
        return (
            <View style={styles.container}>
                <View style={styles.header}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <PlatformIcon size={16} color={PLATFORM_COLORS[platform]} />
                        <Text style={styles.headerUsername}>@{username}</Text>
                    </View>
                </View>
                <View style={styles.loadingWrap}>
                    <ActivityIndicator size="large" color={THEME.primary} />
                    <Text style={styles.loadingText}>{t('app.postPicker.fetchingPosts')}</Text>
                </View>
            </View>
        );
    }

    if (!posts || posts.length === 0) {
        return (
            <View style={styles.container}>
                <View style={styles.header}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <PlatformIcon size={16} color={PLATFORM_COLORS[platform]} />
                        <Text style={styles.headerUsername}>@{username}</Text>
                    </View>
                    <TouchableOpacity onPress={onClose}>
                        <Text style={{ color: THEME.textSecondary, fontSize: 14 }}>{queueRemaining > 0 ? t('app.postPicker.skip') : t('app.postPicker.close')}</Text>
                    </TouchableOpacity>
                </View>
                <Text style={styles.emptyText}>{t('app.postPicker.noPosts')}</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <PlatformIcon size={16} color={PLATFORM_COLORS[platform]} />
                    <Text style={styles.headerUsername}>@{username}</Text>
                    <Text style={styles.headerCount}>{t('app.postPicker.selectedOf', { selected: selectedCount, total: posts.length })}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <TouchableOpacity onPress={onSelectAll}>
                        <Text style={{ color: THEME.primary, fontSize: 13, fontWeight: '600' }}>
                            {allSelected ? t('app.postPicker.deselectAll') : t('app.postPicker.selectAll')}
                        </Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={onClose}>
                        <Text style={{ color: THEME.textSecondary, fontSize: 13 }}>{queueRemaining > 0 ? t('app.postPicker.skip') : t('app.postPicker.close')}</Text>
                    </TouchableOpacity>
                </View>
            </View>

            {/* Grid */}
            <View style={[styles.grid, { gap: hasImages ? 4 : 12 }]}>
                {posts.map((post) => {
                    const isSelected = selectedIds.has(post.platform_post_id);
                    return hasImages ? (
                        <ImagePostCard
                            key={post.platform_post_id}
                            post={post}
                            isSelected={isSelected}
                            onToggle={() => onToggle(post.platform_post_id)}
                            columns={columns}
                            t={t}
                        />
                    ) : (
                        <TextPostCard
                            key={post.platform_post_id}
                            post={post}
                            isSelected={isSelected}
                            onToggle={() => onToggle(post.platform_post_id)}
                            columns={columns}
                        />
                    );
                })}
            </View>

            {/* Footer: Auto-monitor toggle + Confirm */}
            <View style={styles.footer}>
                <View style={styles.toggleRow}>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.toggleLabel}>{t('app.postPicker.autoMonitor')}</Text>
                        <Text style={styles.toggleDesc}>{t('app.postPicker.autoMonitorDesc')}</Text>
                    </View>
                    <Switch
                        value={autoMonitor}
                        onValueChange={onAutoMonitorChange}
                        trackColor={{ false: 'rgba(255,255,255,0.15)', true: THEME.primary + '60' }}
                        thumbColor={autoMonitor ? THEME.primary : '#888'}
                    />
                </View>
                <TouchableOpacity
                    style={[styles.confirmBtn, selectedCount === 0 && { opacity: 0.4 }]}
                    onPress={onConfirm}
                    disabled={selectedCount === 0}
                >
                    <Text style={styles.confirmBtnText}>
                        {selectedCount !== 1 ? t('app.postPicker.trackPosts', { count: selectedCount }) : t('app.postPicker.trackPost', { count: selectedCount })}
                    </Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

/* Image post card (Instagram / TikTok) */
const ImagePostCard = ({ post, isSelected, onToggle, columns, t }) => {
    const size = `${100 / columns}%`;
    const thumbnail = post.thumbnail_url || (post.media_urls && post.media_urls[0]);

    return (
        <TouchableOpacity
            style={[styles.imageCard, { width: size, aspectRatio: 1 }, isSelected && styles.imageCardSelected]}
            onPress={onToggle}
            activeOpacity={0.8}
        >
            {thumbnail ? (
                <img
                    src={thumbnail}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                />
            ) : (
                <View style={styles.imagePlaceholder}>
                    <Text style={{ color: THEME.textTertiary, fontSize: 11 }} numberOfLines={4}>
                        {post.content || (t ? t('app.postPicker.noPreview') : 'No preview')}
                    </Text>
                </View>
            )}

            {/* Hover overlay with stats */}
            <div className="post-overlay" style={overlayStyle}>
                <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                        <FaHeart size={11} color="#fff" />
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>{formatNum(post.latest_likes)}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                        <FaComment size={11} color="#fff" />
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>{formatNum(post.latest_comments)}</Text>
                    </View>
                </View>
                {post.post_url && (
                    <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); window.open(post.post_url, '_blank'); }}
                        style={{ marginTop: 8, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                    >
                        <FaExternalLinkAlt size={10} color="#fff" />
                        <Text style={{ color: '#fff', fontSize: 11 }}>{t ? t('app.postPicker.viewOriginal') : 'View Original'}</Text>
                    </TouchableOpacity>
                )}
            </div>

            {/* Selection badge */}
            {isSelected && (
                <View style={styles.checkBadge}>
                    <FaCheck size={10} color="#fff" />
                </View>
            )}

            {/* Dimmer for unselected */}
            {!isSelected && <View style={styles.dimOverlay} />}

            {/* CSS for hover */}
            <style dangerouslySetInnerHTML={{ __html: `
                .post-overlay { opacity: 0; transition: opacity 0.2s ease; }
                .post-overlay:hover { opacity: 1; }
            `}} />
        </TouchableOpacity>
    );
};

/* Text post card (Twitter / Telegram) */
const TextPostCard = ({ post, isSelected, onToggle, columns }) => {
    const { t } = useTranslation();
    const size = `${100 / columns}%`;
    const isTelegram = post.platform === 'telegram';
    const platColor = isTelegram ? PLATFORM_COLORS.telegram : PLATFORM_COLORS.twitter;
    const PIcon = isTelegram ? FaTelegram : FaTwitter;

    return (
        <TouchableOpacity
            style={[styles.textCard, { width: size }, isSelected && { borderColor: THEME.primary, backgroundColor: THEME.primary + '08' }]}
            onPress={onToggle}
            activeOpacity={0.8}
        >
            {/* Selection indicator */}
            <View style={{
                position: 'absolute', top: 10, right: 10, width: 22, height: 22, borderRadius: 11,
                backgroundColor: isSelected ? THEME.primary : 'transparent',
                borderWidth: isSelected ? 0 : 1.5, borderColor: 'rgba(255,255,255,0.2)',
                justifyContent: 'center', alignItems: 'center', zIndex: 10,
            }}>
                {isSelected && <FaCheck size={10} color="#fff" />}
            </View>

            {/* Platform + Date header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <PIcon size={12} color={platColor} />
                <Text style={{ color: platColor, fontSize: 11, fontWeight: '600' }}>{formatDate(post.posted_at)}</Text>
                {post.post_url && (
                    <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); window.open(post.post_url, '_blank'); }}
                        style={{ marginLeft: 'auto', padding: 2, marginRight: 28 }}
                    >
                        <FaExternalLinkAlt size={9} color={THEME.textTertiary} />
                    </TouchableOpacity>
                )}
            </View>

            {/* Content */}
            <Text style={{ color: THEME.textMain, fontSize: 13, lineHeight: 19 }} numberOfLines={4}>
                {post.content || t('app.postPicker.noContent')}
            </Text>

            {/* Engagement stats bar */}
            <View style={{
                flexDirection: 'row', gap: 14, marginTop: 10, paddingTop: 10,
                borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)',
            }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <FaHeart size={11} color="#ff453a" />
                    <Text style={{ color: THEME.textSecondary, fontSize: 12, fontWeight: '500' }}>{formatNum(post.latest_likes)}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <FaComment size={11} color={platColor} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 12, fontWeight: '500' }}>{formatNum(post.latest_comments)}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <FaShare size={11} color={THEME.success} />
                    <Text style={{ color: THEME.textSecondary, fontSize: 12, fontWeight: '500' }}>{formatNum(post.latest_shares)}</Text>
                </View>
            </View>
        </TouchableOpacity>
    );
};

const overlayStyle = {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
};

const getStyles = () => StyleSheet.create({
    container: {
        backgroundColor: THEME.card,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 18,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.08)',
        padding: 18,
        marginBottom: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14,
    },
    headerUsername: {
        color: THEME.textMain,
        fontSize: 15,
        fontWeight: '700',
    },
    headerCount: {
        color: THEME.textSecondary,
        fontSize: 13,
    },
    loadingWrap: {
        alignItems: 'center',
        paddingVertical: 40,
        gap: 12,
    },
    loadingText: {
        color: THEME.textSecondary,
        fontSize: 14,
    },
    emptyText: {
        color: THEME.textTertiary,
        fontSize: 14,
        textAlign: 'center',
        paddingVertical: 30,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    imageCard: {
        position: 'relative',
        padding: 2,
        overflow: 'hidden',
    },
    imageCardSelected: {
        borderWidth: 2,
        borderColor: THEME.primary,
        borderRadius: 8,
    },
    imagePlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderRadius: 6,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 8,
    },
    dimOverlay: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: 6,
    },
    checkBadge: {
        position: 'absolute',
        top: 8,
        right: 8,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: THEME.primary,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
    },
    textCard: {
        position: 'relative',
        padding: 14,
        backgroundColor: 'rgba(28, 28, 30, 0.5)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.06)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
    },
    footer: {
        marginTop: 16,
        gap: 14,
    },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 12,
        borderRadius: 10,
        backgroundColor: 'rgba(255,255,255,0.04)',
    },
    toggleLabel: {
        color: THEME.textMain,
        fontSize: 14,
        fontWeight: '600',
    },
    toggleDesc: {
        color: THEME.textTertiary,
        fontSize: 12,
        marginTop: 2,
    },
    confirmBtn: {
        alignItems: 'center',
        paddingVertical: 14,
        borderRadius: 14,
        backgroundImage: `linear-gradient(135deg, ${THEME.success}, #28a745)`,
        boxShadow: `0 4px 16px ${THEME.success}40`,
    },
    confirmBtnText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '700',
    },
});

let styles = getStyles();

export default PostPickerGrid;
